import {
  ErrorCode,
  type CreateSessionLink,
  type SessionLink,
  type SessionLinkCreated,
  sessionLinkCreatedSchema,
  sessionLinkListSchema,
} from '@xphoto/shared';
import { apiFetch } from '../lib/http';

/**
 * Client typé des LIENS D'ACCÈS communautaires (Incrément A2, gestion SUPERADMIN).
 *
 * Comme les autres clients du front, chaque réponse est RE-PARSÉE par son schéma
 * partagé `@xphoto/shared` (« never trust the client »). Session same-origin (cookie)
 * via `apiFetch`. Le jeton BRUT n'est renvoyé qu'à la création
 * (`SessionLinkCreated.secret`), une seule fois — jamais ré-affichable ni logué.
 */

/** Erreur d'appel API : statut HTTP + code d'erreur normalisé. */
export class SessionLinkApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'SessionLinkApiError';
  }
}

/**
 * `true` si l'erreur est un refus de cible (`403 SESSION_LINK_TARGET_INVALID`) :
 * compte superadmin ou compte hors bibliothèque → message dédié côté client.
 */
export function isTargetInvalid(err: unknown): boolean {
  return (
    err instanceof SessionLinkApiError &&
    err.status === 403 &&
    err.code === ErrorCode.SESSION_LINK_TARGET_INVALID
  );
}

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON (ou 204) : code générique.
  }
  return 'UNKNOWN';
}

/** `GET /api/session-links` — liste des liens d'accès (superadmin). */
export async function fetchSessionLinks(signal?: AbortSignal): Promise<SessionLink[]> {
  const res = await apiFetch('/api/session-links', {
    credentials: 'same-origin',
    signal: signal ?? null,
  });
  if (!res.ok) throw new SessionLinkApiError(res.status, await errorCodeOf(res));
  return sessionLinkListSchema.parse(await res.json()).links;
}

/**
 * `POST /api/session-links` — crée un lien pour le compte cible. Renvoie l'item + le
 * jeton BRUT (à composer en URL `?access=<secret>` et à afficher UNE SEULE FOIS).
 * 403 `SESSION_LINK_TARGET_INVALID` si la cible est superadmin ou hors bibliothèque.
 */
export async function createSessionLink(
  userId: string,
  label: string,
): Promise<SessionLinkCreated> {
  const payload: CreateSessionLink = { userId, label };
  const res = await apiFetch('/api/session-links', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new SessionLinkApiError(res.status, await errorCodeOf(res));
  return sessionLinkCreatedSchema.parse(await res.json());
}

/** `DELETE /api/session-links/:id` — révoque (soft) un lien d'accès. */
export async function revokeSessionLink(id: string): Promise<void> {
  const res = await apiFetch(`/api/session-links/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new SessionLinkApiError(res.status, await errorCodeOf(res));
}
